import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { differenceInHours } from "date-fns";
import { Upload, FileCheck, AlertCircle, Clock, ChevronRight, Loader2 } from "lucide-react";
import { useMatters } from "@/hooks/use-supabase-queries";

interface PendingActionsCardProps {
  onOpenMatter?: (matterId: string) => void;
}

export function PendingActionsCard({ onOpenMatter }: PendingActionsCardProps) {
  const { data: matters, isLoading } = useMatters();

  // Derive pending actions from active matters
  const actions = (matters || [])
    .filter(m => m.status === 'active')
    .map(m => {
      const ageHours = differenceInHours(new Date(), new Date(m.updated_at));
      return {
        id: m.id,
        title: ageHours >= 48 ? `Requires attention: ${m.title}` : `Review ${m.title}`,
        matter: `${m.matter_number} - ${m.title}`,
        priority: ageHours >= 48 ? 'high' as const : ageHours >= 24 ? 'medium' as const : 'low' as const,
        type: 'review-required' as const,
      };
    })
    .slice(0, 4);

  const actionIcons = {
    "document-upload": Upload,
    "stage-update": FileCheck,
    "review-required": AlertCircle,
    deadline: Clock,
  };

  const priorityStyles = {
    high: "border-l-destructive bg-destructive/5",
    medium: "border-l-accent bg-accent/5",
    low: "border-l-muted-foreground bg-muted/30",
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-8 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          Pending Actions
          <span className="text-sm font-normal text-muted-foreground">({actions.length})</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {actions.length === 0 ? (
          <p className="text-muted-foreground text-center py-4">No pending actions</p>
        ) : (
          actions.map((action) => {
            const Icon = actionIcons[action.type];
            return (
              <button
                key={action.id}
                type="button"
                onClick={() => onOpenMatter?.(action.id)}
                className={`w-full p-3 rounded-lg border-l-4 text-left ${priorityStyles[action.priority]} cursor-pointer hover:opacity-80 transition-opacity`}
              >
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-full bg-background">
                    <Icon className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-foreground text-sm">{action.title}</h4>
                    <p className="text-xs text-muted-foreground truncate">{action.matter}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                </div>
              </button>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}
